import React, { Component } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Progress,
    Button,
    UncontrolledTooltip,
} from "reactstrap";
import { PulseLoader } from "react-spinners";
import api from "./../../../../Api.js";
import { connect } from 'react-redux';

class UnitSkillsReport extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            loading: true,
            error: null,
            reportData: null,
            selectedClassroom: 'all',
            selectedSkillLevel: 'all',
            selectedBlockedStatus: 'all', 
            sortBy: 'name',
            sortDirection: 'asc',

            detailStudent: null,
            detailModalOpen: false
        };
    }

    async componentDidMount() {
        if (this.props.unitId) {
            await this.loadReport();
        }
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.unitId !== this.props.unitId && this.props.unitId) {
            await this.loadReport();
        }
    }

    async loadReport() {
        try {
            // limpar dados antigos imediatamente para mostrar apenas o "carregando"
            this.setState({ loading: true, error: null, reportData: null });

            const [reportRes, classesRes] = await Promise.all([
                api.getUnitSkillsReport(this.props.unitId),
                api.getUserClassrooms(this.props.user.id)
            ]);

            const report = reportRes?.data || {};
            const classesUser = Array.isArray(classesRes?.data) ? classesRes.data : [];

            const allowedIds = new Set(classesUser.map(c => Number(c.id)));

            const originalClassrooms = Array.isArray(report.classrooms) ? report.classrooms : [];
            const filteredClassrooms = originalClassrooms.filter(c => allowedIds.has(Number(c.id)));

            const originalSkills = Array.isArray(report.skillsReport) ? report.skillsReport : [];
            const filteredSkills = originalSkills.filter(s => allowedIds.has(Number(s.classroomId)));

            const overallStats = this.computeOverallStats(filteredSkills);

            // se a turma selecionada não é mais permitida, reseta para 'all'
            const selectedId = this.state.selectedClassroom;
            const keepSelection = selectedId === 'all' || allowedIds.has(Number(selectedId));

            this.setState({
                reportData: {
                    unit: report.unit || {},
                    lessons: Array.isArray(report.lessons) ? report.lessons : [],
                    classrooms: filteredClassrooms,
                    skillsReport: filteredSkills,
                    overallStats
                },
                selectedClassroom: keepSelection ? selectedId : 'all',
                loading: false
            });
        } catch (error) {
            console.error('Erro ao carregar relatório:', error);
            this.setState({
                error: 'Erro ao carregar relatório de habilidades',
                loading: false,
                reportData: null
            });
        }
    }

    computeOverallStats(skillsReport = []) {
        const levels = ['Não Iniciado', 'Iniciante', 'Intermediário', 'Avançado'];
        const dist = { 'Não Iniciado': 0, 'Iniciante': 0, 'Intermediário': 0, 'Avançado': 0 };
        let sumCompletion = 0;

        for (const s of skillsReport) {
            const lvl = s?.summary?.skillLevel;
            if (levels.includes(lvl)) dist[lvl] += 1;
            sumCompletion += Number(s?.summary?.avgProgress) || 0;
        }

        const total = skillsReport.length;
        const avg = total ? Math.round(sumCompletion / total) : 0;

        return {
            totalStudents: total,
            avgCompletionRate: avg,
            skillDistribution: dist
        };
    }

    getSkillLevelColor(level) {
        const colors = {
            'Não Iniciado': 'secondary',
            'Iniciante': 'warning',
            'Intermediário': 'info',
            'Avançado': 'success'
        };
        return colors[level] || 'secondary';
    }

    getSkillLevelIcon(level) {
        const icons = {
            'Não Iniciado': 'fa-minus-circle',
            'Iniciante': 'fa-play-circle',
            'Intermediário': 'fa-star-half-alt',
            'Avançado': 'fa-star'
        };
        return icons[level] || 'fa-question-circle';
    }

    formatTime(minutes) {
        if (minutes < 60) {
            return `${minutes}min`;
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
    }

    getFilteredStudents() {
        if (!this.state.reportData) return [];
        
        let students = [...this.state.reportData.skillsReport];
        
        // Filtrar por turma
        if (this.state.selectedClassroom !== 'all') {
            students = students.filter(s => s.classroomId === parseInt(this.state.selectedClassroom));
        }
        
        // Filtrar por nível de habilidade
        if (this.state.selectedSkillLevel !== 'all') {
            students = students.filter(s => s.summary.skillLevel === this.state.selectedSkillLevel);
        }
        
        // Filtrar por status de bloqueio
        if (this.state.selectedBlockedStatus !== 'all') {
            if (this.state.selectedBlockedStatus === 'blocked') {
                students = students.filter(s => s.studentBlocked === 1);
            } else if (this.state.selectedBlockedStatus === 'active') {
                students = students.filter(s => s.studentBlocked === 0);
            }
        }
        
        // Ordenar
        students.sort((a, b) => {
            let valueA, valueB;
            
            switch (this.state.sortBy) {
                case 'name':
                    valueA = a.studentName.toLowerCase();
                    valueB = b.studentName.toLowerCase();
                    break;
                case 'completion':
                    valueA = a.summary.avgProgress;
                    valueB = b.summary.avgProgress;
                    break;
                case 'skillLevel':
                    const levels = { 'Não Iniciado': 0, 'Iniciante': 1, 'Intermediário': 2, 'Avançado': 3 };
                    valueA = levels[a.summary.skillLevel];
                    valueB = levels[b.summary.skillLevel];
                    break;
                case 'timeSpent':
                    valueA = a.summary.totalTimeSpent;
                    valueB = b.summary.totalTimeSpent;
                    break;
                case 'status': // Novo ordenação por status
                    valueA = a.studentBlocked === 1 ? 1 : 0;
                    valueB = b.studentBlocked === 1 ? 1 : 0;
                    break;
                default:
                    valueA = a.studentName.toLowerCase();
                    valueB = b.studentName.toLowerCase();
            }
            
            if (this.state.sortDirection === 'asc') {
                return valueA > valueB ? 1 : -1;
            } else {
                return valueA < valueB ? 1 : -1;
            }
        });
        
        return students;
    }

    toggleSort(field) {
        if (this.state.sortBy === field) {
            this.setState({
                sortDirection: this.state.sortDirection === 'asc' ? 'desc' : 'asc'
            });
        } else {
            this.setState({
                sortBy: field,
                sortDirection: 'asc'
            });
        }
    }

    exportToCSV() {
        const students = this.getFilteredStudents();
        const csvData = [];
        
        // Cabeçalho
        csvData.push([
            'Nome',
            'Email',
            'Turma',
            'Status',
            'Nível de Habilidade',
            'Taxa de Conclusão (%)',
            'Lições Completadas',
            'Total de Lições',
            'Tempo Total (min)',
            'Nota Média Quiz'
        ]);
        
        // Dados
        students.forEach(student => {
            csvData.push([
                student.studentName,
                student.studentEmail,
                student.classroomName,
                student.studentBlocked === 1 ? 'Bloqueado' : 'Ativo',
                student.summary.skillLevel,
                student.summary.avgProgress,
                student.summary.completedLessons,
                student.summary.totalLessons,
                student.summary.totalTimeSpent,
                student.summary.avgQuizScore
            ]);
        });
        
        // Converter para CSV
        const csvContent = csvData.map(row => 
            row.map(field => `"${field}"`).join(',')
        ).join('\n');
        
        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        // nome seguro
        const unitNameSafe = (this.state.reportData?.unit?.name || 'unit').replace(/\s+/g, '_');
        link.download = `relatorio_habilidades_${unitNameSafe}.csv`;
        link.click();
    }

    // Motivo do nível geral do aluno
    getStudentSkillReason(student) {
        if (!student || !student.summary) return '';
        const lessons = Array.isArray(student.lessons) ? student.lessons : [];
        const total = student.summary.totalLessons || lessons.length || 0;
        const done = student.summary.completedLessons || lessons.filter(l => l.isCompleted).length || 0;

        const adv = lessons.filter(l => l.skillLevel === 'Avançado').length;
        const inter = lessons.filter(l => l.skillLevel === 'Intermediário').length;
        const notStarted = lessons.filter(l => l.skillLevel === 'Não Iniciado').length;
        const completed = total - notStarted;
        const advRate = total ? Math.round((adv * 100) / total) : 0;
        const interAdvRateAmongCompleted = completed ? Math.round(((adv + inter) * 100) / completed) : 0;

        switch (student.summary.skillLevel) {
            case 'Avançado':
                return `≥ 70% das lições concluídas no nível Avançado (${adv}/${total}, ${advRate}%).`;
            case 'Intermediário':
                return `Entre as lições concluídas, ≥ 50% são Intermediário ou Avançado (${interAdvRateAmongCompleted}%).`;
            case 'Iniciante':
                if (done === 0) return 'Pouca ou nenhuma lição concluída.';
                return 'A maioria das lições concluídas está no nível Iniciante.';
            default:
                return 'Nenhuma lição concluída na unidade.';
        }
    }

    // Destaques: lições em que foi melhor (prioriza Avançado e maior quizScore)
    getStudentStrengths(student, limit = 2) {
        if (!student || !Array.isArray(student.lessons)) return [];
        const advanced = student.lessons
            .filter(l => l.skillLevel === 'Avançado')
            .sort((a, b) => (b.quizScore || 0) - (a.quizScore || 0));
        const pick = advanced.slice(0, limit);
        return pick.map(l => {
            const name = l.lessonName || l.name || `Lição ${l.lessonId}`;
            if (l.quizScore && l.quizScore > 0) return `${name} (${l.quizScore}%)`;
            return name;
        });
    }

    render() {
        const { isOpen, onClose } = this.props;
        const { loading, error, reportData } = this.state;

        return (
            <Modal 
                isOpen={isOpen} 
                toggle={onClose} 
                size="xl" 
                className="modal-dialog-scrollable"
                style={{ maxWidth: '95vw', width: '95vw' }}
            >
                <ModalHeader 
                    toggle={onClose}
                    style={{ 
                        background: 'white',
                        color: '#32325d',
                        borderBottom: '1px solid #e9ecef'
                    }}
                >
                    <i className="fas fa-user-graduate mr-3"></i>
                    <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                        {(this.props.translate['relatorio_de_habilidades'] || 'Relatório de Habilidades')} - {reportData?.unit?.name || (this.props.translate['carregando'] || 'Carregando...')}
                    </span>

                    {/* Ajuda dos cálculos de níveis */}
                    <Button
                        id="unitSkillsInfoHelp"
                        color="link"
                        size="sm"
                        className="ml-3 p-0 text-muted"
                        style={{ textDecoration: 'none' }}
                    >
                        <i className="fas fa-info-circle"></i>
                    </Button>
                    <UncontrolledTooltip placement="left" target="unitSkillsInfoHelp">
                        <div style={{ textAlign: 'left', maxWidth: 320 }}>
                            <strong>{this.props.translate['como_calculamos_os_niveis'] || 'Como calculamos os níveis'}</strong>
                            <br />
                            <u>{this.props.translate['por_licao'] || 'Por lição'}</u>
                            <ul className="mb-1">
                                <li>{this.props.translate['regra_quiz'] || 'Quiz: Avançado ≥ 80%, Intermediário 60–79%, Iniciante < 60%.'}</li>
                                <li>{this.props.translate['regra_outras_licoes'] || 'Outras lições: Avançado se ≥ 2 conclusões e tempo > 5 min; Intermediário se ≥ 1 conclusão e tempo > 2 min; Iniciante nos demais casos.'}</li>
                                <li>{this.props.translate['regra_sem_registros'] || 'Não Iniciado: sem registros.'}</li>
                            </ul>
                            <u>{this.props.translate['por_aluno_geral'] || 'Por aluno (geral)'}</u>    
                            <ul className="mb-0">
                                <li>{this.props.translate['nivel_nao_iniciado_regra'] || 'Não Iniciado: 0 lições concluídas.'}</li>
                                <li>{this.props.translate['nivel_avancado_regra'] || 'Avançado: ≥ 70% das lições concluídas no nível Avançado.'}</li>
                                <li>{this.props.translate['nivel_intermediario_regra'] || '(Avançado + Intermediário) ≥ 50% das lições concluídas.'}</li>
                                <li>{this.props.translate['nivel_iniciante_regra'] || 'Iniciante: demais casos.'}</li>
                            </ul>
                            <small className="d-block mt-1">{this.props.translate['tempo_considera_soma'] || 'Tempo considera a soma do campo “time” por lição.'}</small>
                        </div>
                    </UncontrolledTooltip>
                </ModalHeader>
                
                <ModalBody style={{ 
                    maxHeight: '85vh', 
                    overflowY: 'auto',
                    background: '#f8f9fe',
                    padding: '1.5rem'
                }}>
                    {loading && (
                        <div className="text-center py-5" style={{ 
                            background: 'white', 
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                        }}>
                            <PulseLoader size={15} color="#5e72e4" />
                            <p className="mt-3 text-muted">{this.props.translate['carregando_relatorio_de_habilidades'] || 'Carregando relatório de habilidades...'}</p>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger" style={{ 
                            borderRadius: '8px',
                            border: 'none'
                        }}>
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            {error}
                        </div>
                    )}

                    {reportData && (
                        <Container fluid style={{ padding: '0' }}>
                            {/* Resumo Geral - Corrigindo estatísticas de bloqueio */}
                            <Row className="mb-4">
                                <Col lg="12">
                                    <Card style={{ 
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                    }}>
                                        <CardHeader style={{ 
                                            background: '#f8f9fe',
                                            borderBottom: '1px solid #e9ecef',
                                            padding: '1rem 1.5rem'
                                        }}>
                                            <h5 className="mb-0" style={{ color: '#32325d', fontWeight: '600' }}>
                                                <i className="fas fa-chart-pie mr-2"></i>
                                                {this.props.translate['resumo_geral'] || 'Resumo Geral'}
                                            </h5>
                                        </CardHeader>
                                        <CardBody style={{ padding: '1.5rem' }}>
                                            <Row>
                                                {/* Cards de estatísticas principais */}
                                                <Col xl="2" lg="4" md="6" className="mb-3">
                                                    <div style={{ 
                                                        textAlign: 'center',
                                                        padding: '1.5rem 1rem',
                                                        borderRadius: '8px',
                                                        background: 'white',
                                                        border: '1px solid #e9ecef',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <i className="fas fa-users text-primary mb-2" style={{ fontSize: '2rem' }}></i>
                                                        <h3 className="mb-1" style={{ fontWeight: '600', color: '#32325d' }}>
                                                            {reportData.overallStats.totalStudents}
                                                        </h3>
                                                        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                                                            {this.props.translate['total_de_alunos'] || 'Total de Alunos'}
                                                        </p>
                                                    </div>
                                                </Col>
                                                
                                                <Col xl="2" lg="4" md="6" className="mb-3">
                                                    <div style={{ 
                                                        textAlign: 'center',
                                                        padding: '1.5rem 1rem',
                                                        borderRadius: '8px',
                                                        background: 'white',
                                                        border: '1px solid #e9ecef',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <i className="fas fa-user-check text-success mb-2" style={{ fontSize: '2rem' }}></i>
                                                        <h3 className="mb-1" style={{ fontWeight: '600', color: '#32325d' }}>
                                                            {reportData.skillsReport.filter(s => s.studentBlocked === 0).length}
                                                        </h3>
                                                        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                                                            {this.props.translate['alunos_ativos'] || 'Alunos Ativos'}
                                                        </p>
                                                    </div>
                                                </Col>
                                                
                                                <Col xl="2" lg="4" md="6" className="mb-3">
                                                    <div style={{ 
                                                        textAlign: 'center',
                                                        padding: '1.5rem 1rem',
                                                        borderRadius: '8px',
                                                        background: 'white',
                                                        border: '1px solid #e9ecef',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <i className="fas fa-user-times text-danger mb-2" style={{ fontSize: '2rem' }}></i>
                                                        <h3 className="mb-1" style={{ fontWeight: '600', color: '#32325d' }}>
                                                            {reportData.skillsReport.filter(s => s.studentBlocked === 1).length}
                                                        </h3>
                                                        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                                                            {this.props.translate['alunos_bloqueados'] || 'Alunos Bloqueados'}
                                                        </p>
                                                    </div>
                                                </Col>
                                                
                                                <Col xl="2" lg="4" md="6" className="mb-3">
                                                    <div style={{ 
                                                        textAlign: 'center',
                                                        padding: '1.5rem 1rem',
                                                        borderRadius: '8px',
                                                        background: 'white',
                                                        border: '1px solid #e9ecef',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <i className="fas fa-chart-line text-success mb-2" style={{ fontSize: '2rem' }}></i>
                                                        <h3 className="mb-1" style={{ fontWeight: '600', color: '#32325d' }}>
                                                            {reportData.overallStats.avgCompletionRate}%
                                                        </h3>
                                                        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                                                            {this.props.translate['taxa_media_de_conclusao'] || 'Taxa Média de Conclusão'}
                                                        </p>
                                                    </div>
                                                </Col>
                                                
                                                <Col xl="2" lg="4" md="6" className="mb-3">
                                                    <div style={{ 
                                                        textAlign: 'center',
                                                        padding: '1.5rem 1rem',
                                                        borderRadius: '8px',
                                                        background: 'white',
                                                        border: '1px solid #e9ecef',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <i className="fas fa-book-open text-info mb-2" style={{ fontSize: '2rem' }}></i>
                                                        <h3 className="mb-1" style={{ fontWeight: '600', color: '#32325d' }}>
                                                            {reportData.lessons.length}
                                                        </h3>
                                                        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                                                            {this.props.translate['total_de_licoes'] || 'Total de Lições'}
                                                        </p>
                                                    </div>
                                                </Col>
                                                
                                                <Col xl="2" lg="4" md="6" className="mb-3">
                                                    <div style={{ 
                                                        textAlign: 'center',
                                                        padding: '1.5rem 1rem',
                                                        borderRadius: '8px',
                                                        background: 'white',
                                                        border: '1px solid #e9ecef',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <i className="fas fa-star text-warning mb-2" style={{ fontSize: '2rem' }}></i>
                                                        <h3 className="mb-1" style={{ fontWeight: '600', color: '#32325d' }}>
                                                            {reportData.overallStats.skillDistribution['Avançado'] || 0}
                                                        </h3>
                                                        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                                                            {this.props.translate['alunos_avancados'] || 'Alunos Avançados'}
                                                        </p>
                                                    </div>
                                                </Col>
                                            </Row>

                                            {/* Distribuição de Habilidades */}
                                            <Row className="mt-4">
                                                <Col lg="12">
                                                    <h6 className="mb-3" style={{ 
                                                        color: '#32325d', 
                                                        fontWeight: '600',
                                                        borderLeft: '3px solid #5e72e4',
                                                        paddingLeft: '0.75rem'
                                                    }}>
                                                        {this.props.translate['distribuicao_por_nivel_de_habilidade'] || 'Distribuição por Nível de Habilidade'}
                                                    </h6>
                                                    <Row>
                                                        {Object.entries(reportData.overallStats.skillDistribution).map(([level, count]) => (
                                                            <Col lg="3" md="6" key={level} className="mb-3">
                                                                <div style={{ 
                                                                    textAlign: 'center',
                                                                    padding: '1rem',
                                                                    borderRadius: '8px',
                                                                    background: 'white',
                                                                    border: `1px solid ${level === 'Avançado' ? '#28a745' : 
                                                                             level === 'Intermediário' ? '#17a2b8' :
                                                                             level === 'Iniciante' ? '#ffc107' : '#6c757d'}`,
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                                }}>
                                                                    <Badge 
                                                                        color={this.getSkillLevelColor(level)} 
                                                                        className="mb-2"
                                                                        style={{ fontSize: '0.75rem' }}
                                                                    >
                                                                        <i className={`fas ${this.getSkillLevelIcon(level)} mr-1`}></i>
                                                                        {level}
                                                                    </Badge>
                                                                    <h4 className="mb-1" style={{ fontWeight: '600', color: '#32325d' }}>
                                                                        {count}
                                                                    </h4>
                                                                    <small className="text-muted">
                                                                        {reportData.overallStats.totalStudents > 0 
                                                                            ? Math.round((count / reportData.overallStats.totalStudents) * 100)
                                                                            : 0}% dos alunos
                                                                    </small>
                                                                </div>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Filtros - Mantendo os filtros */}
                            <Row className="mb-4">
                                <Col lg="12">
                                    <Card style={{ 
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                    }}>
                                        <CardBody style={{ padding: '1rem' }}>
                                            <Row className="align-items-center">
                                                <Col lg="2" md="6" className="mb-2">
                                                    <label className="form-control-label" style={{ 
                                                        fontSize: '0.875rem', 
                                                        fontWeight: '600',
                                                        color: '#32325d'
                                                    }}>
                                                        {this.props.translate['filtrar_por_turma'] || 'Filtrar por Turma'}
                                                    </label>
                                                    <select 
                                                        className="form-control"
                                                        value={this.state.selectedClassroom}
                                                        onChange={(e) => this.setState({ selectedClassroom: e.target.value })}
                                                        style={{ 
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    >
                                                        <option value="all">{this.props.translate['todas_as_turmas'] || 'Todas as Turmas'}</option>
                                                        {reportData.classrooms.map(classroom => (
                                                            <option key={classroom.id} value={classroom.id}>
                                                                {classroom.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </Col>
                                                
                                                <Col lg="2" md="6" className="mb-2">
                                                    <label className="form-control-label" style={{ 
                                                        fontSize: '0.875rem', 
                                                        fontWeight: '600',
                                                        color: '#32325d'
                                                    }}>
                                                        {this.props.translate['status_do_aluno'] || 'Status do Aluno'}
                                                    </label>
                                                    <select 
                                                        className="form-control"
                                                        value={this.state.selectedBlockedStatus}
                                                        onChange={(e) => this.setState({ selectedBlockedStatus: e.target.value })}
                                                        style={{ 
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    >
                                                        <option value="all">{this.props.translate['todos_os_status'] || 'Todos os Status'}</option>
                                                        <option value="active">{this.props.translate['apenas_ativos'] || 'Apenas Ativos'}</option>
                                                        <option value="blocked">{this.props.translate['apenas_bloqueados'] || 'Apenas Bloqueados'}</option>
                                                    </select>
                                                </Col>
                                                
                                                <Col lg="2" md="6" className="mb-2">
                                                    <label className="form-control-label" style={{ 
                                                        fontSize: '0.875rem', 
                                                        fontWeight: '600',
                                                        color: '#32325d'
                                                    }}>
                                                        {this.props.translate['nivel_de_habilidade'] || 'Nível de Habilidade'}
                                                    </label>
                                                    <select 
                                                        className="form-control"
                                                        value={this.state.selectedSkillLevel}
                                                        onChange={(e) => this.setState({ selectedSkillLevel: e.target.value })}
                                                        style={{ 
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    >
                                                        <option value="all">{this.props.translate['todos_os_niveis'] || 'Todos os Níveis'}</option>
                                                        <option value="Não Iniciado">{this.props.translate['nao_iniciado'] || 'Não Iniciado'}</option>
                                                        <option value="Iniciante">{this.props.translate['iniciante'] || 'Iniciante'}</option>
                                                        <option value="Intermediário">{this.props.translate['intermediario'] || 'Intermediário'}</option>
                                                        <option value="Avançado">{this.props.translate['avancado'] || 'Avançado'}</option>
                                                    </select>
                                                </Col>
                                                
                                                <Col lg="3" md="6" className="mb-2">
                                                    <label className="form-control-label" style={{ 
                                                        fontSize: '0.875rem', 
                                                        fontWeight: '600',
                                                        color: '#32325d'
                                                    }}>
                                                        {this.props.translate['ordenar_por'] || 'Ordenar por'}
                                                    </label>
                                                    <select 
                                                        className="form-control"
                                                        value={this.state.sortBy}
                                                        onChange={(e) => this.setState({ sortBy: e.target.value })}
                                                        style={{ 
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    >
                                                        <option value="name">{this.props.translate['nome_do_aluno'] || 'Nome do Aluno'}</option>
                                                        <option value="completion">{this.props.translate['taxa_de_conclusao'] || 'Taxa de Conclusão'}</option>
                                                        <option value="skillLevel">{this.props.translate['nivel_de_habilidade'] || 'Nível de Habilidade'}</option>
                                                        <option value="timeSpent">{this.props.translate['tempo_investido'] || 'Tempo Investido'}</option>
                                                        <option value="status">{this.props.translate['status'] || 'Status'}</option>
                                                    </select>
                                                </Col>
                                                
                                                <Col lg="3" md="6" className="mb-2 text-center">
                                                    <label className="form-control-label d-block" style={{ 
                                                        fontSize: '0.875rem', 
                                                        fontWeight: '600',
                                                        color: '#32325d'
                                                    }}>&nbsp;</label>
                                                    <Button 
                                                        color="success" 
                                                        size="sm"
                                                        onClick={() => this.exportToCSV()}
                                                        className="btn-block"
                                                        style={{ borderRadius: '4px', fontWeight: '600' }}
                                                    >
                                                        <i className="fas fa-download mr-1"></i>
                                                        {this.props.translate['exportar_csv'] || 'Exportar CSV'}
                                                    </Button>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg="12" className="text-right mt-2">
                                                    <small className="text-muted">
                                                        {(this.props.translate['mostrando'] || 'Mostrando')} {this.getFilteredStudents().length} {(this.props.translate['de'] || 'de')} {reportData.skillsReport.length} {(this.props.translate['alunos'] || 'alunos')}
                                                    </small>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Tabela de Alunos - Adicionando coluna de status */}
                            <Row>
                                <Col lg="12">
                                    <Card style={{ 
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                    }}>
                                        <CardHeader style={{ 
                                            background: '#f8f9fe',
                                            borderBottom: '1px solid #e9ecef',
                                            padding: '1rem 1.5rem'
                                        }}>
                                            <h5 className="mb-0" style={{ color: '#32325d', fontWeight: '600' }}>
                                                <i className="fas fa-users mr-2"></i>
                                                {(this.props.translate['detalhes_por_aluno'] || 'Detalhes por Aluno')} ({this.getFilteredStudents().length})
                                            </h5>
                                        </CardHeader>
                                        <CardBody className="p-0">
                                            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                                <Table responsive className="table-flush mb-0">
                                                    <thead style={{ 
                                                        position: 'sticky', 
                                                        top: 0, 
                                                        zIndex: 10,
                                                        background: '#f8f9fe'
                                                    }}>
                                                        <tr>
                                                            <th 
                                                                className="cursor-pointer border-0"
                                                                onClick={() => this.toggleSort('name')}
                                                                style={{ 
                                                                    minWidth: '250px',
                                                                    padding: '1rem',
                                                                    fontWeight: '600',
                                                                    color: '#32325d',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {this.props.translate['aluno'] || 'Aluno'} 
                                                                {this.state.sortBy === 'name' && (
                                                                    <i className={`fas fa-sort-${this.state.sortDirection === 'asc' ? 'up' : 'down'} ml-1 text-primary`}></i>
                                                                )}
                                                            </th>
                                                            <th className="border-0" style={{ 
                                                                minWidth: '120px',
                                                                padding: '1rem',
                                                                fontWeight: '600',
                                                                color: '#32325d',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {this.props.translate['turma'] || 'Turma'}
                                                            </th>
                                                            <th 
                                                                className="cursor-pointer border-0"
                                                                onClick={() => this.toggleSort('status')}
                                                                style={{ 
                                                                    minWidth: '100px',
                                                                    padding: '1rem',
                                                                    fontWeight: '600',
                                                                    color: '#32325d',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {this.props.translate['status'] || 'Status'}
                                                                {this.state.sortBy === 'status' && (
                                                                    <i className={`fas fa-sort-${this.state.sortDirection === 'asc' ? 'up' : 'down'} ml-1 text-primary`}></i>
                                                                )}
                                                            </th>
                                                            <th 
                                                                className="cursor-pointer border-0"
                                                                onClick={() => this.toggleSort('skillLevel')}
                                                                style={{ 
                                                                    minWidth: '150px',
                                                                    padding: '1rem',
                                                                    fontWeight: '600',
                                                                    color: '#32325d',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {this.props.translate['nivel_de_habilidade'] || 'Nível de Habilidade'}
                                                                {this.state.sortBy === 'skillLevel' && (
                                                                    <i className={`fas fa-sort-${this.state.sortDirection === 'asc' ? 'up' : 'down'} ml-1 text-primary`}></i>
                                                                )}
                                                            </th>
                                                            <th 
                                                                className="cursor-pointer border-0"
                                                                onClick={() => this.toggleSort('completion')}
                                                                style={{ 
                                                                    minWidth: '180px',
                                                                    padding: '1rem',
                                                                    fontWeight: '600',
                                                                    color: '#32325d',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {this.props.translate['progresso'] || 'Progresso'}
                                                                {this.state.sortBy === 'completion' && (
                                                                    <i className={`fas fa-sort-${this.state.sortDirection === 'asc' ? 'up' : 'down'} ml-1 text-primary`}></i>
                                                                )}
                                                            </th>
                                                            <th className="border-0" style={{ 
                                                                minWidth: '100px',
                                                                padding: '1rem',
                                                                fontWeight: '600',
                                                                color: '#32325d',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {this.props.translate['licoes'] || 'Lições'}
                                                            </th>
                                                            <th 
                                                                className="cursor-pointer border-0"
                                                                onClick={() => this.toggleSort('timeSpent')}
                                                                style={{ 
                                                                    minWidth: '120px',
                                                                    padding: '1rem',
                                                                    fontWeight: '600',
                                                                    color: '#32325d',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {this.props.translate['tempo'] || 'Tempo'}
                                                                {this.state.sortBy === 'timeSpent' && (
                                                                    <i className={`fas fa-sort-${this.state.sortDirection === 'asc' ? 'up' : 'down'} ml-1 text-primary`}></i>
                                                                )}
                                                            </th>
                                                            <th className="border-0" style={{ 
                                                                minWidth: '100px',
                                                                padding: '1rem',
                                                                fontWeight: '600',
                                                                color: '#32325d',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {this.props.translate['quizzes'] || 'Quizzes'}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.getFilteredStudents().map((student, index) => {
                                                            const reason = this.getStudentSkillReason(student);
                                                            const strengths = this.getStudentStrengths(student);
                                                            return (
                                                            <tr 
                                                                key={`${student.classroomId}-${student.studentId}`}
                                                                style={{ 
                                                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fe',
                                                                    opacity: student.studentBlocked === 1 ? 0.7 : 1
                                                                }}
                                                            >
                                                                <td style={{ padding: '1rem' }}>
                                                                    <div className="media align-items-center">
                                                                        <span className="avatar avatar-sm rounded-circle mr-3">
                                                                            <img 
                                                                                alt="Avatar do aluno"
                                                                                src={student.studentPhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(student.studentName)}&background=5e72e4&color=fff`}
                                                                                className="rounded-circle"
                                                                                style={{ 
                                                                                    width: '40px', 
                                                                                    height: '40px',
                                                                                    filter: student.studentBlocked === 1 ? 'grayscale(100%)' : 'none'
                                                                                }}
                                                                            />
                                                                        </span>
                                                                        <div className="media-body">
                                                                            <span className="name mb-0 text-sm font-weight-600" style={{ 
                                                                                color: student.studentBlocked === 1 ? '#6c757d' : '#32325d'
                                                                            }}>
                                                                                {student.studentName}
                                                                                {student.studentBlocked === 1 && (
                                                                                    <i className="fas fa-lock ml-2 text-danger" title="Usuário bloqueado"></i>
                                                                                )}
                                                                            </span>
                                                                            <br />
                                                                            <small className="text-muted">{student.studentEmail}</small>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    <Badge color="primary" className="badge-lg" style={{ fontSize: '0.75rem' }}>
                                                                        {student.classroomName}
                                                                    </Badge>
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    <Badge 
                                                                        color={student.studentBlocked === 1 ? 'danger' : 'success'}
                                                                        className="d-inline-flex align-items-center"
                                                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', lineHeight: 1 }}
                                                                    >
                                                                        <i className={`fas ${student.studentBlocked === 1 ? 'fa-user-times' : 'fa-user-check'} mr-1`}></i>
                                                                        {student.studentBlocked === 1 ? (this.props.translate['bloqueado'] || 'Bloqueado') : (this.props.translate['ativo'] || 'Ativo')}
                                                                    </Badge>
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    <Badge 
                                                                        color={this.getSkillLevelColor(student.summary.skillLevel)}
                                                                        style={{ fontSize: '0.75rem' }}
                                                                    >
                                                                        <i className={`fas ${this.getSkillLevelIcon(student.summary.skillLevel)} mr-1`}></i>
                                                                        {student.summary.skillLevel}
                                                                    </Badge>

                                                                    {/* Botão para abrir detalhes */}
                                                                    <Button
                                                                        color="link"
                                                                        size="sm"
                                                                        className="p-0 ml-2"
                                                                        style={{ textDecoration: 'none' }}
                                                                        onClick={() => this.setState({ detailStudent: student, detailModalOpen: true })}
                                                                    >
                                                                        <i className="fas fa-info-circle mr-1"></i>
                                                                        Ver detalhes
                                                                    </Button>
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    <div className="d-flex align-items-center">
                                                                        <span className="mr-2 font-weight-bold" style={{ 
                                                                            minWidth: '40px',
                                                                            color: student.summary.avgProgress >= 80 ? '#28a745' :
                                                                                   student.summary.avgProgress >= 60 ? '#ffc107' : '#dc3545'
                                                                        }}>
                                                                            {student.summary.avgProgress}%
                                                                        </span>
                                                                        <div className="flex-fill">
                                                                            <Progress 
                                                                                max="100" 
                                                                                value={student.summary.avgProgress}
                                                                                color={
                                                                                    student.summary.avgProgress >= 80 ? 'success' :
                                                                                    student.summary.avgProgress >= 60 ? 'warning' : 'danger'
                                                                                }
                                                                                style={{ height: '8px' }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                                    <span style={{ 
                                                                        fontSize: '1rem',
                                                                        fontWeight: '600',
                                                                        color: '#28a745'
                                                                    }}>
                                                                        {student.summary.completedLessons}
                                                                    </span>
                                                                    <span style={{ 
                                                                        fontSize: '0.875rem',
                                                                        color: '#6c757d'
                                                                    }}>
                                                                        /{student.summary.totalLessons}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    <Badge color="info" className="d-inline-flex align-items-center" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', lineHeight: 1 }}>
                                                                        <i className="fas fa-clock mr-1"></i>
                                                                        {this.formatTime(student.summary.totalTimeSpent)}
                                                                    </Badge>
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    {student.summary.avgQuizScore > 0 ? (
                                                                        <Badge 
                                                                            color={
                                                                                student.summary.avgQuizScore >= 80 ? 'success' :
                                                                                student.summary.avgQuizScore >= 60 ? 'warning' : 'danger'
                                                                            }
                                                                            style={{ fontSize: '0.75rem' }}
                                                                        >
                                                                            {student.summary.avgQuizScore}%
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge color="secondary" style={{ fontSize: '0.75rem' }}>
                                                                            N/A
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )})}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {this.getFilteredStudents().length === 0 && (
                                                <div className="text-center py-5">
                                                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                                                    <h5 className="text-muted">{this.props.translate['nenhum_aluno_encontrado'] || 'Nenhum aluno encontrado'}</h5>
                                                    <p className="text-muted">
                                                        {this.props.translate['tente_ajustar_os_filtros'] || 'Tente ajustar os filtros para ver mais resultados.'}
                                                    </p>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Detalhes do Aluno - Modal separado */}
                            <Modal 
                                isOpen={this.state.detailModalOpen} 
                                toggle={() => this.setState({ detailModalOpen: false })} 
                                size="lg" 
                                className="modal-dialog-scrollable"
                                style={{ maxWidth: '90vw', width: '90vw' }}
                            >
                                <ModalHeader 
                                    toggle={() => this.setState({ detailModalOpen: false })}
                                    style={{ 
                                        background: 'white',
                                        color: '#32325d',
                                        borderBottom: '1px solid #e9ecef'
                                    }}
                                >
                                    <i className="fas fa-user-graduate mr-3"></i>
                                    <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                                        {this.props.translate['detalhes_do_aluno'] || 'Detalhes do Aluno'} - {this.state.detailStudent?.studentName || 'Carregando...'}
                                    </span>
                                </ModalHeader>
                                <ModalBody style={{ 
                                    background: '#f8f9fe',
                                    padding: '1.5rem',
                                    borderRadius: '0.5rem'
                                }}>
                                    {this.state.detailStudent && (
                                        <Row>
                                            <Col lg="12">
                                                <Card style={{ 
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                                }}>
                                                    <CardBody style={{ padding: '1.5rem' }}>
                                                        <div className="mb-4">
                                                            <h6 className="mb-3" style={{ 
                                                                color: '#32325d', 
                                                                fontWeight: '600',
                                                                borderLeft: '3px solid #5e72e4',
                                                                paddingLeft: '0.75rem'
                                                            }}>
                                                                {this.props.translate['informacoes_basicas'] || 'Informações Básicas'}
                                                            </h6>
                                                            <div className="row">
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['nome'] || 'Nome'}:</strong> {this.state.detailStudent.studentName}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['email'] || 'Email'}:</strong> {this.state.detailStudent.studentEmail}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['turma'] || 'Turma'}:</strong> {this.state.detailStudent.classroomName}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['status'] || 'Status'}:</strong>&nbsp;
                                                                    <Badge color={this.state.detailStudent.studentBlocked === 1 ? 'danger' : 'success'} className="d-inline-flex align-items-center" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', lineHeight: 1 }}>
                                                                        <i className={`fas ${this.state.detailStudent.studentBlocked === 1 ? 'fa-user-times' : 'fa-user-check'} mr-1`}></i>
                                                                        {this.state.detailStudent.studentBlocked === 1 ? (this.props.translate['bloqueado'] || 'Bloqueado') : (this.props.translate['ativo'] || 'Ativo')}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <h6 className="mb-3" style={{ 
                                                                color: '#32325d', 
                                                                fontWeight: '600',
                                                                borderLeft: '3px solid #5e72e4',
                                                                paddingLeft: '0.75rem'
                                                            }}>
                                                                {this.props.translate['desempenho'] || 'Desempenho'}
                                                            </h6>
                                                            <div className="row">
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['nivel_de_habilidade'] || 'Nível de Habilidade'}:</strong> {this.state.detailStudent.summary.skillLevel}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['motivo'] || 'Motivo'}:</strong> {this.getStudentSkillReason(this.state.detailStudent) || '—'}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['progresso'] || 'Progresso'}:</strong> 
                                                                    <div className="d-flex align-items-center">
                                                                        <span className="mr-2 font-weight-bold" style={{ 
                                                                            minWidth: '40px',
                                                                            color: this.state.detailStudent.summary.avgProgress >= 80 ? '#28a745' :
                                                                                   this.state.detailStudent.summary.avgProgress >= 60 ? '#ffc107' : '#dc3545'
                                                                        }}>
                                                                            {this.state.detailStudent.summary.avgProgress}%
                                                                        </span>
                                                                        <div className="flex-fill">
                                                                            <Progress 
                                                                                max="100" 
                                                                                value={this.state.detailStudent.summary.avgProgress}
                                                                                color={
                                                                                    this.state.detailStudent.summary.avgProgress >= 80 ? 'success' :
                                                                                    this.state.detailStudent.summary.avgProgress >= 60 ? 'warning' : 'danger'
                                                                                }
                                                                                style={{ height: '8px' }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['licoes_concluidas'] || 'Lições Concluídas'}:</strong> 
                                                                    <span style={{ 
                                                                        fontSize: '1rem',
                                                                        fontWeight: '600',
                                                                        color: '#28a745'
                                                                    }}>
                                                                        {this.state.detailStudent.summary.completedLessons}
                                                                    </span>
                                                                    <span style={{ 
                                                                        fontSize: '0.875rem',
                                                                        color: '#6c757d'
                                                                    }}>
                                                                        /{this.state.detailStudent.summary.totalLessons}
                                                                    </span>
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['tempo_total'] || 'Tempo Total'}:</strong>&nbsp;
                                                                    <Badge color="info" className="d-inline-flex align-items-center" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', lineHeight: 1 }}>
                                                                        <i className="fas fa-clock mr-1"></i>
                                                                        {this.formatTime(this.state.detailStudent.summary.totalTimeSpent)}
                                                                    </Badge>
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <strong>{this.props.translate['nota_media_quiz'] || 'Nota Média Quiz'}:</strong> 
                                                                    {this.state.detailStudent.summary.avgQuizScore > 0 ? (
                                                                        <Badge 
                                                                            color={
                                                                                this.state.detailStudent.summary.avgQuizScore >= 80 ? 'success' :
                                                                                this.state.detailStudent.summary.avgQuizScore >= 60 ? 'warning' : 'danger'
                                                                            }
                                                                            style={{ fontSize: '0.75rem' }}
                                                                        >
                                                                            {this.state.detailStudent.summary.avgQuizScore}%
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge color="secondary" style={{ fontSize: '0.75rem' }}>
                                                                            N/A
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>
                                    )}
                                </ModalBody>
                            </Modal>
                        </Container>
                    )}
                </ModalBody>
            </Modal>
        );
    }
}

export default connect(state => ({
  languages: state.language,
  translate: state.translate,
  accesslevel: state.accesslevel,
  permission: state.permission,
  user: state.user,
}))(UnitSkillsReport);
